import axios from 'axios';

import { getApiEndPoint } from '../helpers/apibase';
import { logger } from './loggerService';

// Dual-write of phone numbers to property-management (AI-1293).
// See docs/007-phone-number-dual-write-to-property-management.md for the failure/ordering strategy.

const baseURL = getApiEndPoint();
export const propertyManagementApi = axios.create({
  baseURL: `${baseURL}/propertymanagement`,
  withCredentials: true,
  timeout: 15000,
});

export const PHONE_NUMBER_SYNC_FAILED_KEY = 'Phone_number_sync_failed';

export type PhoneNumberDualWriteSource = 'store' | 'property-create' | 'property-edit';

export type PhoneNumberDualWriteResult = 'synced' | 'skipped' | 'failed';

export type SyncPropertyDirectPhoneNumberArgs = {
  orgId?: string;
  propertyId?: string;
  phoneNumber?: string | null;
  source: PhoneNumberDualWriteSource;
};

/**
 * Mirrors a phone number already saved to Zeus/RMS onto the property's direct phone number in
 * property-management. Never throws: failures are logged and reported as `'failed'` so callers can
 * warn the user without failing the legacy save. Without an org/property there is nothing to sync
 * to, so the write is `'skipped'` (logged, but not worth warning the user about).
 */
export const syncPropertyDirectPhoneNumber = async ({
  orgId,
  propertyId,
  phoneNumber,
  source,
}: SyncPropertyDirectPhoneNumberArgs): Promise<PhoneNumberDualWriteResult> => {
  if (!orgId || !propertyId) {
    logger.warn('Property phone number dual-write skipped: missing org or property id', {
      orgId,
      propertyId,
      source,
    });
    return 'skipped';
  }

  try {
    await propertyManagementApi.post(
      `/orgs/${orgId}/properties/${propertyId}/direct-phone-number`,
      { directPhoneNumber: phoneNumber?.trim() || null }
    );
    return 'synced';
  } catch (e) {
    logger.error('Property phone number dual-write to property-management failed', {
      orgId,
      propertyId,
      source,
      status: e?.response?.status,
      error: e?.response?.data?.error ?? e?.message,
    });
    return 'failed';
  }
};
